import { getCookie, setCookie, config } from './constants';

function normalizeResponse(response) {
    if (!response.ok) {
        // Return a standardized error for HTTP errors (403, 500, 502, 503, etc.)
        return { success: false, errors: ['ERR_HTTP_' + response.status], data: null, httpStatus: response.status };
    }
    return response.json().catch(() => {
        // Handle invalid JSON responses
        return { success: false, errors: ['ERR_INVALID_RESPONSE'], data: null };
    });
}

// --- CSRF Token Management ---
let csrfToken = null;
let csrfTokenPromise = null;
let csrfRefreshPromise = null; // shared promise for concurrent CSRF refresh deduplication
let csrfApiUrl = null;   // remember the API base URL for token refresh

export function fetchCsrfToken(apiUrl) {
    if (apiUrl) csrfApiUrl = apiUrl;
    const url = csrfApiUrl || apiUrl;
    if (!url) return Promise.resolve(null);
    if (csrfTokenPromise) return csrfTokenPromise;
    const sid = getCookie(config.sessionCookieName);
    const headers = { 'Content-Type': 'application/json;charset=utf-8' };
    if (sid) headers['X-Session-Id'] = sid;
    csrfTokenPromise = fetch(url + 'GetCsrfToken', {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
    })
        .then(res => res.json())
        .then(data => {
            if (data.success && data.data && data.data.csrfToken) {
                csrfToken = data.data.csrfToken;
                // Server may return a new sessionId (e.g. when the old one was deprecated)
                if (data.data.sessionId) {
                    setCookie(config.sessionCookieName, data.data.sessionId, 365);
                }
            }
            return csrfToken;
        })
        .catch((err) => {
            csrfTokenPromise = null; // only clear on error so it can be retried
            return null;
        });
    return csrfTokenPromise;
}

// Returns a promise that resolves when a CSRF token is available (fetches one if needed)
export function ensureCsrfToken(apiUrl) {
    if (csrfToken) return Promise.resolve(csrfToken);
    return fetchCsrfToken(apiUrl);
}

export function getCsrfToken() {
    return csrfToken;
}

export function clearCsrfToken() {
    csrfToken = null;
    csrfTokenPromise = null;
}

// Refreshes the CSRF token exactly once even when called concurrently by multiple requests
function refreshCsrfToken() {
    if (csrfRefreshPromise) return csrfRefreshPromise;
    csrfToken = null;
    csrfTokenPromise = null;
    csrfRefreshPromise = fetchCsrfToken()
        .then(token => { csrfRefreshPromise = null; return token; })
        .catch(() => { csrfRefreshPromise = null; return null; });
    return csrfRefreshPromise;
}

function isCsrfError(responseData) {
    if (!responseData || responseData.success) return false;
    const errors = responseData.errors;
    if (typeof errors === 'string') {
        return errors === 'ERR_CSRF_TOKEN_MISSING' || errors === 'ERR_CSRF_TOKEN_INVALID';
    }
    if (Array.isArray(errors)) {
        return errors.some(e => e === 'ERR_CSRF_TOKEN_MISSING' || e === 'ERR_CSRF_TOKEN_INVALID');
    }
    return false;
}

function isNotLoggedInError(responseData) {
    if (!responseData || responseData.success) return false;
    const errors = responseData.errors;
    if (typeof errors === 'string') {
        return errors === 'ERR_NOT_LOGGED_IN';
    }
    if (Array.isArray(errors)) {
        return errors.some(e => e === 'ERR_NOT_LOGGED_IN');
    }
    return false;
}

function handleNotLoggedIn() {
    // Clear session and redirect to login
    setCookie(config.sessionCookieName, '', -1);
    clearCsrfToken();
    window.location.href = config.frontendUrl;
}

function httpRequest(method, url, params, authToken, _isRetry) {
    //alert ("fetch " + url);
    const fetchConfig = {
        method,
        headers: {},
    };
    if (params) {
        fetchConfig.body = JSON.stringify(params);
    }
    if (["POST", "DELETE", "PUT", "PATCH"].indexOf(method.toUpperCase()) !== -1) {
        fetchConfig.headers["Content-Type"] = "application/json;charset=utf-8";
    }
    if (csrfToken) {
        fetchConfig.headers["X-CSRF-Token"] = csrfToken;
    }
    const sid = getCookie(config.sessionCookieName);
    if (sid) {
        fetchConfig.headers["X-Session-Id"] = sid;
    }
    if (authToken) {
        fetchConfig.headers.Authorization = authToken;
        fetchConfig.headers["Api-Token"] = authToken;
    }

    return fetch(`${url}`, fetchConfig)
        .then(normalizeResponse)
        .then((data) => {
            // Auto-retry once on CSRF token errors
            if (!_isRetry && isCsrfError(data)) {
                return refreshCsrfToken().then(() => {
                    return httpRequest(method, url, params, authToken, true);
                });
            }
            // Session expired: redirect to login
            if (isNotLoggedInError(data)) {
                handleNotLoggedIn();
                return data;
            }
            return data;
        })
        .catch(() => {
            return { success: false, errors: ['ERR_NETWORK'], data: null };
        });
}


function rawFetch(url, formData, _isRetry) {
    const headers = {};
    if (csrfToken) {
        headers['X-CSRF-Token'] = csrfToken;
        formData.set('csrfToken', csrfToken);
    }
    const sid = getCookie(config.sessionCookieName);
    if (sid) {
        headers['X-Session-Id'] = sid;
    }
    return fetch(`${url}`, {
        method: 'POST',
        headers,
        body: formData})
        .then(normalizeResponse)
        .then((data) => {
            // Auto-retry once on CSRF token errors
            if (!_isRetry && isCsrfError(data)) {
                return refreshCsrfToken().then(() => {
                    return rawFetch(url, formData, true);
                });
            }
            // Session expired: redirect to login
            if (isNotLoggedInError(data)) {
                handleNotLoggedIn();
                return data;
            }
            return data;
        })
        .catch(() => {
            return { success: false, errors: ['ERR_NETWORK'], data: null };
        });
}

function httpRequestBlob(url, params, _isRetry) {
    const fetchConfig = {
        method: 'POST',
        headers: { 'Content-Type': 'application/json;charset=utf-8' },
    };
    if (params) fetchConfig.body = JSON.stringify(params);
    if (csrfToken) fetchConfig.headers['X-CSRF-Token'] = csrfToken;
    const sid = getCookie(config.sessionCookieName);
    if (sid) fetchConfig.headers['X-Session-Id'] = sid;

    return fetch(url, fetchConfig)
        .then(response => {
            const contentType = response.headers.get('Content-Type') || '';
            // JSON response = error or CSRF issue (server always returns HTTP 200 with JSON on failure)
            if (contentType.includes('application/json')) {
                return response.json().catch(() => ({ success: false, errors: ['ERR_INVALID_RESPONSE'] })).then(data => {
                    if (!_isRetry && isCsrfError(data)) {
                        return refreshCsrfToken().then(() => httpRequestBlob(url, params, true));
                    }
                    if (isNotLoggedInError(data)) {
                        handleNotLoggedIn();
                    }
                    return data;
                });
            }
            if (!response.ok) {
                return { success: false, errors: ['ERR_HTTP_' + response.status] };
            }
            return response.blob().then(blob => {
                const disposition = response.headers.get('Content-Disposition') || '';
                const match = disposition.match(/filename="?([^"]+)"?/);
                const filename = match ? match[1] : 'report';
                return { success: true, blob, filename };
            });
        })
        .catch(() => ({ success: false, errors: ['ERR_NETWORK'] }));
}

export default class API {
    static get(url, authToken) {
        return httpRequest("GET", url, null, authToken);
    }
    static put(url, params, authToken) {
        return httpRequest("PUT", url, params, authToken);
    }
    static post(url, params, authToken) {
        return httpRequest("POST", url, params, authToken);
    }
    static delete(url, authToken) {
        return httpRequest("DELETE", url, null, authToken);
    }
    static patch(url, params) {
        return httpRequest("PATCH", url, params);
    }
    static formPost(url, body) {
        return rawFetch(url, body);
    }
    static postStream(url, params) {
        return httpRequestBlob(url, params);
    }
}
